import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, Calendar, CalendarCheck, Users, 
  MessageSquare, Settings, LogOut, Menu, X, Leaf, BookOpen, Apple, Euro, Network,
  ShieldAlert, Ghost, AlertTriangle, CreditCard, Download
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Tableau de bord', end: true },
  { to: '/admin/slots', icon: Calendar, label: 'Créneaux' },
  { to: '/admin/appointments', icon: CalendarCheck, label: 'Rendez-vous' },
  { to: '/admin/patients', icon: Users, label: 'Patients' },
  { to: '/admin/network', icon: Network, label: 'Réseau & Partenaires' },
  { to: '/admin/library', icon: BookOpen, label: 'Bibliothèque' },
  { to: '/admin/diet-templates', icon: Apple , label: 'Modèles alimentaires' },
  { to: '/admin/accounting', icon: Euro, label: 'Comptabilité' },
  { to: '/admin/sms', icon: MessageSquare, label: 'SMS' },
  { to: '/admin/settings', icon: Settings, label: 'Paramètres' },

]

export default function AdminLayout() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [subPlans, setSubPlans] = useState({ monthly: 39, annual: 390 }) // <-- Valeurs par défaut en cas de problème de récupération depuis Stripe

  // --- CALCUL ALARME ESSAI GRATUIT - trialing ---
  const trialDaysLeft = user?.workspace_created_at 
      ? Math.max(0, 14 - Math.floor((new Date() - new Date(user.workspace_created_at)) / (1000 * 60 * 60 * 24)))
      : 14;
  const showTrialWarning = user?.subscription_status === 'trialing' && trialDaysLeft <= 5;

  useEffect(() => {
      // On charge les prix en arrière-plan au cas où on aurait besoin d'afficher le péage
      api.get('/api/workspaces/subscription-plans')
         .then(res => setSubPlans(res.data))
         .catch(() => {})
  }, [])
  // Détection du mode Fantôme
  const isGhostMode = !!localStorage.getItem('super_admin_token')

  const handleQuitGhostMode = () => {
      const saToken = localStorage.getItem('super_admin_token')
      localStorage.setItem('token', saToken)
      localStorage.removeItem('super_admin_token')
      window.location.href = '/admin/superadmin' // On force le rechargement total
  }

  useEffect(() => {
    // Vérifier l'authentification
    const token = localStorage.getItem('token')
    if (!token) {
      navigate('/admin/login')
      return
    }

    // Récupérer les infos utilisateur
    api.get('/api/auth/me')
      .then(res => {
          setUser(res.data)
          // THE GOD MODE : Dès que l'admin est reconnu, on force le Header Axios global !
          // Ça annule le fallback "laetitia-lacroix" de l'IP locale.
          api.defaults.headers.common['X-Workspace-Id'] = res.data.workspace_id
      })
      .catch(() => {
        localStorage.removeItem('token')
        navigate('/admin/login')
      })
  }, [navigate])

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('super_admin_token') // <-- Purge définitive du mode fantôme !
    navigate('/admin/login')
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sage-50">
        <div className="spinner" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-4 border-b border-gray-200">
          <div className="w-10 h-10 bg-gradient-to-br from-sun-300 to-peach-100 rounded-full flex items-center justify-center">
            <Leaf className="w-5 h-5 text-sage-600" />
          </div>
          <div>
            <h1 className="font-serif text-sage-700 font-semibold">BellaBlue</h1>
            <p className="text-xs text-sage-500">Administration</p>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-160px)] scrollbar-hide">
          
          {/* Menu Super Admin (Visible uniquement si droits SA et pas en mode fantôme) */}
          {user.is_superadmin && !isGhostMode && (
              <NavLink
                  to="/admin/superadmin"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors mb-4 border-2 shadow-sm font-bold ${isActive ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-gray-800 border-gray-800 text-white hover:bg-gray-700'}`}
              >
                  <ShieldAlert className="w-5 h-5" /> Tour de Contrôle (SaaS)
              </NavLink>
          )}

          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                ${isActive 
                  ? 'bg-sage-100 text-sage-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <item.icon className="w-5 h-5" />
              <div className="flex-1 flex justify-between items-center">
                  <span>{item.label}</span>
                  {item.to === '/admin/settings' && showTrialWarning && (
                      <span className="flex h-2.5 w-2.5 relative">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                      </span>
                  )}
              </div>
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-sage-200 rounded-full flex items-center justify-center">
              <span className="text-sage-700 font-medium">
                {user.name?.charAt(0) || 'L'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{user.name}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors w-full px-2 py-2 rounded-lg hover:bg-gray-100"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen relative">
        
        {/* BANNIÈRE ROUGE GHOST MODE */}
        {isGhostMode && (
            <div className="bg-red-600 text-white px-4 py-2 flex justify-between items-center z-[100] shadow-md sticky top-0">
                <div className="flex items-center gap-2 font-bold text-sm">
                    <Ghost className="w-5 h-5 animate-pulse" />
                    MODE FANTÔME : Connecté en tant que {user.name}
                </div>
                <button onClick={handleQuitGhostMode} className="bg-white text-red-600 hover:bg-red-50 px-3 py-1 rounded text-xs font-bold transition-colors shadow-sm">
                    Quitter le mode
                </button>
            </div>
        )}

        {/* Top bar mobile */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:hidden">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="ml-4 font-serif text-sage-700">BellaBlue</h1>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto relative">
          
          {/* LE GATEKEEPER SAAS (PÉAGE) */}
          {user && ['canceled', 'past_due', 'unpaid'].includes(user.subscription_status) ? (
              <div className="max-w-2xl mx-auto mt-10">
                  <div className="bg-white rounded-[2rem] shadow-2xl border border-red-100 p-8 md:p-12 text-center animate-in zoom-in-95">
                      <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                          <AlertTriangle className="w-10 h-10 text-red-500" />
                      </div>
                      <h2 className="text-3xl font-serif font-bold text-gray-800 mb-4">Abonnement suspendu</h2>
                      <p className="text-gray-600 mb-8 text-lg">
                          L'accès à votre espace de gestion est temporairement bloqué suite à un problème de paiement ou à l'expiration de votre abonnement. <br/><br/>
                          Régularisez votre situation pour retrouver l'accès immédiat à vos dossiers patients.
                      </p>
                      
                      <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                          <button 
                              onClick={async () => {
                                  const toastId = toast.loading("Redirection vers la passerelle sécurisée...");
                                  try {
                                      const res = await api.post('/api/workspaces/create-checkout-session', { plan_type: 'monthly', return_url: window.location.origin })
                                      window.location.href = res.data.url
                                  } catch(e) { toast.error("Erreur de connexion", { id: toastId }) }
                              }}
                              className="btn bg-white text-sage-700 border-2 border-sage-300 hover:bg-sage-50 px-6 py-4 flex items-center gap-2"
                          >
                              <CreditCard className="w-5 h-5"/>
                              Mensuel ({subPlans.monthly}€)
                          </button>

                          <button 
                              onClick={async () => {
                                  const toastId = toast.loading("Redirection vers la passerelle sécurisée...");
                                  try {
                                      const res = await api.post('/api/workspaces/create-checkout-session', { plan_type: 'annual', return_url: window.location.origin })
                                      window.location.href = res.data.url
                                  } catch(e) { toast.error("Erreur de connexion", { id: toastId }) }
                              }}
                              className="btn bg-sage-600 text-white hover:bg-sage-700 shadow-lg px-6 py-4 flex items-center justify-center gap-2 relative"
                          >
                              <div className="absolute -top-3 right-[-10px] bg-orange-500 text-white text-[10px] font-black px-2 py-1 rounded-full shadow-sm rotate-12">
                                  2 MOIS OFFERTS !
                              </div>
                              Annuel ({subPlans.annual}€)
                          </button>
                          
                          <button 
                              onClick={async () => {
                                  const toastId = toast.loading("Ouverture du portail client...");
                                  try {
                                      const res = await api.post('/api/workspaces/customer-portal')
                                      window.location.href = res.data.url
                                  } catch(e) { 
                                      toast.error("Erreur", { id: toastId }) 
                                  }
                              }}
                              className="btn bg-gray-800 text-white hover:bg-black px-8 py-4"
                          >
                              Mettre à jour ma CB
                          </button>
                      </div>
                      
                      <div className="mt-8 pt-8 border-t border-gray-100 flex flex-col items-center">
                          <p className="text-xs text-gray-500 mb-3">Vos données vous appartiennent. Vous pouvez les récupérer à tout moment.</p>
                          <button 
                              onClick={async () => {
                                  const toastId = toast.loading("Préparation de vos archives (ZIP)...")
                                  try {
                                      const response = await api.get('/api/admin/export-rgpd', { responseType: 'blob' })
                                      const url = window.URL.createObjectURL(new Blob([response.data]))
                                      const link = document.createElement('a')
                                      link.href = url
                                      link.setAttribute('download', `Export_BellaBlue.zip`)
                                      document.body.appendChild(link)
                                      link.click()
                                      link.remove()
                                      toast.success("Archive téléchargée !", { id: toastId })
                                  } catch (error) { toast.error("Erreur d'export", { id: toastId }) }
                              }}
                              className="text-sm text-sage-600 hover:text-sage-800 font-bold flex items-center gap-2 transition-colors"
                          >
                              <Download className="w-4 h-4"/> Exporter toutes mes données (RGPD)
                          </button>
                      </div>
                  </div>
              </div>
          ) : (
              <Outlet />
          )}

        </main>
      </div>
    </div>
  )
}
